import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import CtaBand from "@/components/CtaBand";
import { Reveal } from "@/components/motion";
import { serviceBySlug, categories, services } from "@/data/services";
import { usePageMeta } from "@/lib/api";
import NotFoundPage from "./NotFoundPage";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const svc = serviceBySlug(slug);
  usePageMeta(
    svc ? `${svc.name} | SarvaRitam ESG Advisory` : "Service not found | SarvaRitam",
    svc ? svc.short : undefined
  );
  if (!svc) return <NotFoundPage />;

  const cat = categories.find((c) => c.id === svc.category);
  const related = services.filter((s) => s.category === svc.category && s.slug !== slug).slice(0, 3);

  return (
    <>
      <section className="bg-forest text-offwhite relative grain overflow-hidden pt-40 pb-16 lg:pt-48" data-testid="service-hero">
        <div className="absolute inset-0 pointer-events-none opacity-30" style={{ background: "radial-gradient(55% 70% at 80% 0%, rgba(198,161,91,0.45) 0%, transparent 65%)" }} />
        <div className="wrap relative">
          <Reveal>
            <nav className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest2 text-white/70 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-gold">Home</Link> <ChevronRight size={11} />
              <Link to="/services" className="hover:text-gold">Services</Link> <ChevronRight size={11} />
              <span className="text-gold">{cat.name}</span>
            </nav>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="eyebrow text-gold mb-5">{cat.name}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="h-display font-semibold text-[34px] sm:text-5xl lg:text-[60px] leading-[1.05] max-w-3xl">{svc.name}</h1>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 text-white/90 text-lg leading-relaxed max-w-2xl">{svc.short}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-offwhite text-charcoal sec" data-testid="service-body">
        <div className="wrap grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7 space-y-14">
            <Reveal>
              <div>
                <p className="eyebrow text-forest mb-4">What is it?</p>
                <p className="text-[16.5px] leading-relaxed text-charcoal/80">{svc.what}</p>
              </div>
            </Reveal>
            <Reveal>
              <div>
                <p className="eyebrow text-forest mb-4">Why does it matter?</p>
                <p className="text-[16.5px] leading-relaxed text-charcoal/80">{svc.why}</p>
              </div>
            </Reveal>
            <Reveal>
              <div>
                <p className="eyebrow text-forest mb-5">Who needs it?</p>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {svc.who.map((w) => (
                    <li key={w} className="flex items-start gap-3 bg-white/60 border rule-light p-4 text-[14px] font-semibold text-charcoal/80">
                      <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 shrink-0" /> {w}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal>
              <div>
                <p className="eyebrow text-forest mb-5">What SarvaRitam does</p>
                <ul className="space-y-3.5">
                  {svc.do.map((d, i) => (
                    <li key={d} className="flex items-start gap-4">
                      <span className="font-mono text-[11px] text-gold mt-1.5">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-[15px] text-charcoal/80 leading-relaxed">{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal>
              <div>
                <p className="eyebrow text-forest mb-5">Engagement process</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {svc.process.map((p, i) => (
                    <div key={p} className="border rule-light bg-white/60 p-5">
                      <span className="font-mono text-[11px] text-sage">{String(i + 1).padStart(2, "0")}</span>
                      <p className="text-[13.5px] font-bold mt-2 leading-snug">{p}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 space-y-6">
              <Reveal>
                <div className="bg-charcoal text-offwhite p-8 sm:p-10" data-testid="service-deliverables">
                  <p className="eyebrow text-gold mb-6">What is delivered</p>
                  <ul className="space-y-3.5">
                    {svc.deliverables.map((d) => (
                      <li key={d} className="flex items-start gap-3 text-[14.5px] text-white/96">
                        <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 shrink-0" /> {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <div className="border rule-light bg-white/60 p-8">
                  <p className="eyebrow text-forest mb-5">Frameworks that may apply</p>
                  <div className="flex flex-wrap gap-2">
                    {svc.frameworks.map((f) => (
                      <Link key={f} to="/insights" className="font-mono text-[11.5px] text-forest border rule-light px-3 py-1.5 hover:border-gold hover:text-charcoal transition-colors">
                        {f}
                      </Link>
                    ))}
                  </div>
                  <p className="text-[11px] text-charcoal/45 mt-4 leading-relaxed">Applicability depends on organisation type, jurisdiction and reporting period.</p>
                </div>
              </Reveal>
              <Reveal delay={0.12}>
                <div className="bg-gold/15 border border-gold/40 p-8">
                  <p className="font-display font-semibold text-lg text-charcoal mb-2">Next step</p>
                  <p className="text-[14px] text-charcoal/70 leading-relaxed mb-6">A 30-minute introductory conversation is the fastest way to scope this properly.</p>
                  <Link to={`/contact?service=${encodeURIComponent(svc.name)}`} className="btn-forest w-full justify-center" data-testid="service-detail-cta">
                    {svc.next} <ArrowUpRight size={15} />
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-offwhite text-charcoal pb-20">
          <div className="wrap">
            <p className="eyebrow text-forest mb-8">Related services</p>
            <div className="grid md:grid-cols-3 gap-4">
              {related.map((r, i) => (
                <Reveal key={r.slug} delay={i * 0.06}>
                  <Link to={`/services/${r.slug}`} className="lift group block border rule-light bg-white/60 hover:border-forest p-7 rounded-sm" data-testid={`related-service-${r.slug}`}>
                    <h3 className="font-display font-semibold text-[17px] leading-snug">{r.name}</h3>
                    <span className="inline-flex items-center gap-1.5 mt-4 font-bold text-[12px] text-forest">
                      Learn more <ArrowUpRight size={13} />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
      <CtaBand />
    </>
  );
}
